import { DestroyRef, EffectRef, Injectable, effect } from "@angular/core";
import { Subscription, timeout } from "rxjs";
import { AwaitingOstUploadEventFile, OstUploadStatus, OstUploadSuccessEvent, PendingOstUploadFile, createOstUploadOwnerId } from "../models/images/ost-upload-tracking.model";
import { OstFileResponse } from "../models/ost/ost-file-response.model";
import { FileImageBucketStore } from "../stores/file-image-bucket.store";
import { OstUploadTrackingStore } from "../stores/ost-upload-tracking.store";
import { UserStore } from "../stores/user.store";
import { EventStreamService } from "./event-stream.service";
import { FileImageService } from "./file-image.service";
import { IOstUploadSnapshot, OstUploadPersistenceService } from "./ost-upload-persistence.service";

interface BrowserLockManager {
  request<T>(name: string, options: { mode: "exclusive"; ifAvailable: boolean }, callback: (lock: unknown | null) => Promise<T>): Promise<T>;
}

interface ActiveUploadRequest {
  subscription?: Subscription;
  finish: () => void;
}

const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 5 * 60 * 1_000;
const UPLOAD_REQUEST_TIMEOUT_MS = 30_000;
const UPLOAD_LEASE_MS = 45_000;

@Injectable({ providedIn: "root" })
export class OstUploadCoordinatorService {
  private currentOwnerId?: string;
  private ownerActivation?: { ownerId: string; promise: Promise<void> };
  private ownerGeneration = 0;
  private identityEffect?: EffectRef;
  private started = false;
  private visibleOstNum?: string;
  private visibleOstFiles: OstFileResponse[] = [];
  private uploadEvents?: Subscription;
  private uploadEventOstKey = "";
  private expiryTimer?: number;
  private readonly retryTimers = new Map<string, number>();
  private readonly activeAttempts = new Set<string>();
  private readonly activeRequests = new Map<string, ActiveUploadRequest>();
  private readonly refreshingUsage = new Set<string>();
  private readonly tabId = this.newLocalId();

  constructor(
    private readonly userStore: UserStore,
    private readonly ostUploadPersistenceService: OstUploadPersistenceService,
    private readonly fileImageService: FileImageService,
    private readonly eventStreamService: EventStreamService,
    private readonly uploadTrackingStore: OstUploadTrackingStore,
    private readonly fileImageBucketStore: FileImageBucketStore,
    private readonly destroyRef: DestroyRef
  ) {
    this.destroyRef.onDestroy(() => this.stop());
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener("online", this.handleOnline);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.identityEffect = effect(() => {
      const authenticated = this.userStore.isAuthenticated();
      const jwt = this.userStore.jwt();
      const user = this.userStore.user();
      const generation = ++this.ownerGeneration;

      if (!authenticated || !jwt) {
        this.deactivateOwner();
        return;
      }

      void createOstUploadOwnerId(user).then((ownerId: string | undefined) => {
        if (generation !== this.ownerGeneration) return;
        if (!ownerId) {
          this.deactivateOwner();
          return;
        }
        if (this.currentOwnerId !== ownerId) void this.activateOwner(ownerId);
      });
    });
  }

  stop(): void {
    this.identityEffect?.destroy();
    this.identityEffect = undefined;
    this.started = false;
    window.removeEventListener("online", this.handleOnline);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.deactivateOwner();
  }

  async enqueueUploadBatch(ostNum: string | number, files: File[], confirmedFileNames: string[] = []): Promise<PendingOstUploadFile[]> {
    if (files.length === 0) return [];
    const ownerId = await this.ensureCurrentOwnerId();
    if (!ownerId) throw new Error("The current user could not be identified for durable upload tracking.");

    const pendingFiles = await this.ostUploadPersistenceService.saveOstUploadBatch(
      ownerId,
      ostNum,
      files,
      confirmedFileNames,
      Date.now(),
      files.map((file: File) => file.name)
    );
    await this.refreshOwnerState(ownerId);
    return pendingFiles;
  }

  getStatus(ostNum: string | number | undefined): OstUploadStatus {
    if (ostNum === undefined || ostNum === null) {
      return { hydrated: true, blocked: false, deliveryFileNames: [], awaitingEventFileNames: [], expiredFileNames: [] };
    }
    return this.uploadTrackingStore.getStatus(ostNum);
  }

  onOstVisible(ostNum: string | number, ostFiles: OstFileResponse[] = []): void {
    const normalizedOstNum = String(ostNum);
    this.visibleOstNum = normalizedOstNum;
    this.visibleOstFiles = ostFiles;
    const ownerId = this.currentOwnerId;
    if (!ownerId) return;
    void this.reconcileConfirmedOstFiles(ownerId, normalizedOstNum, ostFiles).then(() => this.refreshOwnerState(ownerId));
  }

  onOstHidden(ostNum?: string | number): void {
    if (ostNum === undefined || this.visibleOstNum === String(ostNum)) {
      this.visibleOstNum = undefined;
      this.visibleOstFiles = [];
    }
  }

  private readonly handleOnline = (): void => {
    const ownerId = this.currentOwnerId;
    if (!ownerId) return;
    void this.refreshOwnerState(ownerId);
  };

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState !== "visible") return;
    const ownerId = this.currentOwnerId;
    if (!ownerId) return;
    void this.refreshOwnerState(ownerId);
  };

  private activateOwner(ownerId: string): Promise<void> {
    if (this.currentOwnerId === ownerId) return Promise.resolve();
    if (this.ownerActivation?.ownerId === ownerId) return this.ownerActivation.promise;
    const promise = this.performOwnerActivation(ownerId).finally(() => {
      if (this.ownerActivation?.promise === promise) this.ownerActivation = undefined;
    });
    this.ownerActivation = { ownerId, promise };
    return promise;
  }

  private async performOwnerActivation(ownerId: string): Promise<void> {
    this.deactivateOwner();
    this.currentOwnerId = ownerId;
    this.uploadTrackingStore.resetForOwner(ownerId);
    if (this.visibleOstNum) await this.reconcileConfirmedOstFiles(ownerId, this.visibleOstNum, this.visibleOstFiles);
    await this.refreshOwnerState(ownerId);
  }

  private async ensureCurrentOwnerId(): Promise<string | undefined> {
    if (this.currentOwnerId) return this.currentOwnerId;
    if (!this.userStore.isAuthenticated() || !this.userStore.jwt()) return undefined;
    const jwt = this.userStore.jwt();
    const ownerId = await createOstUploadOwnerId(this.userStore.user());
    if (!ownerId || !this.userStore.isAuthenticated() || jwt !== this.userStore.jwt()) return undefined;
    await this.activateOwner(ownerId);
    return this.currentOwnerId === ownerId ? ownerId : undefined;
  }

  private deactivateOwner(): void {
    this.ownerGeneration++;
    this.uploadEvents?.unsubscribe();
    this.uploadEvents = undefined;
    this.uploadEventOstKey = "";
    window.clearTimeout(this.expiryTimer);
    this.expiryTimer = undefined;
    this.retryTimers.forEach((timer: number): void => window.clearTimeout(timer));
    this.retryTimers.clear();
    this.activeRequests.forEach((request: ActiveUploadRequest): void => {
      request.subscription?.unsubscribe();
      request.finish();
    });
    this.activeRequests.clear();
    this.activeAttempts.clear();
    this.refreshingUsage.clear();
    this.currentOwnerId = undefined;
    this.uploadTrackingStore.resetForOwner();
  }

  private async refreshOwnerState(ownerId: string): Promise<void> {
    if (ownerId !== this.currentOwnerId) return;

    try {
      await this.ostUploadPersistenceService.expireOstUploadEvents(ownerId);
      const snapshot = await this.ostUploadPersistenceService.getOstUploadSnapshot(ownerId);
      if (ownerId !== this.currentOwnerId) return;
      this.updateStoreFromSnapshot(ownerId, snapshot);
      this.updateUploadEventStream(snapshot);
      this.scheduleDeadline(snapshot.awaitingEvent);
      snapshot.delivery.forEach((file: PendingOstUploadFile): void => this.scheduleDelivery(file));
      snapshot.usageDirty.filter((record) => record.ostNum === this.visibleOstNum).forEach((record): void => void this.refreshDirtyUsage(ownerId, record.ostNum));
    } catch (error) {
      console.error("Failed to load persisted OST upload state:", error);
      if (ownerId === this.currentOwnerId) window.setTimeout(() => void this.refreshOwnerState(ownerId), 5_000);
    }
  }

  private updateStoreFromSnapshot(ownerId: string, snapshot: IOstUploadSnapshot): void {
    const ostNums = new Set<string>([
      ...snapshot.delivery.map((file: PendingOstUploadFile): string => file.ostNum),
      ...snapshot.awaitingEvent.map((file: AwaitingOstUploadEventFile): string => file.ostNum),
      ...snapshot.expired.map((file): string => file.ostNum),
    ]);
    const statuses: Record<string, OstUploadStatus> = {};

    ostNums.forEach((ostNum: string): void => {
      const delivery = snapshot.delivery.filter((file: PendingOstUploadFile): boolean => file.ostNum === ostNum);
      const awaitingEvent = snapshot.awaitingEvent.filter((file: AwaitingOstUploadEventFile): boolean => file.ostNum === ostNum);
      const expired = snapshot.expired.filter((file): boolean => file.ostNum === ostNum);
      statuses[ostNum] = {
        hydrated: true,
        blocked: delivery.length > 0 || awaitingEvent.length > 0,
        deliveryFileNames: delivery.map((file: PendingOstUploadFile): string => file.fileName),
        awaitingEventFileNames: awaitingEvent.map((file: AwaitingOstUploadEventFile): string => file.fileName),
        expiredFileNames: expired.map((file): string => file.fileName),
      };
    });
    this.uploadTrackingStore.setHydrated(ownerId, statuses);
  }

  private updateUploadEventStream(snapshot: IOstUploadSnapshot): void {
    const ostNums = [...new Set([...snapshot.delivery, ...snapshot.awaitingEvent].map((file): string => file.ostNum))].sort();
    const streamKey = ostNums.join(",");
    if (streamKey === this.uploadEventOstKey) return;

    this.uploadEvents?.unsubscribe();
    this.uploadEvents = undefined;
    this.uploadEventOstKey = streamKey;
    if (ostNums.length === 0) return;

    const ownerId = this.currentOwnerId;
    if (!ownerId) return;
    this.uploadEvents = this.eventStreamService.listenUploadSuccessEvents(ostNums).subscribe({
      next: (event): void => {
        if (ownerId === this.currentOwnerId) void this.handleUploadSuccess(ownerId, event);
      },
      error: (error: unknown): void => console.error("Upload-success event stream failed:", error),
    });
  }

  private async handleUploadSuccess(ownerId: string, event: OstUploadSuccessEvent): Promise<void> {
    const { ostNum, fileName } = event;
    try {
      const result = await this.ostUploadPersistenceService.confirmOstUploadFile(ownerId, ostNum, fileName);
      if (result.confirmed) {
        if (ownerId === this.currentOwnerId) await this.refreshOwnerState(ownerId);
      }
    } catch (error) {
      console.error("Failed to correlate OST upload-success event:", error);
    }
  }

  private async reconcileConfirmedOstFiles(ownerId: string, ostNum: string, ostFiles: OstFileResponse[]): Promise<void> {
    try {
      const snapshot = await this.ostUploadPersistenceService.getOstUploadSnapshot(ownerId);
      const knownNames = new Set(
        [...snapshot.delivery, ...snapshot.awaitingEvent, ...snapshot.expired].filter((file): boolean => file.ostNum === ostNum).map((file): string => file.fileName)
      );
      const filesToConfirm = ostFiles.map((file: OstFileResponse): string => file.fileName).filter((fileName: string): boolean => knownNames.has(fileName));
      for (const fileName of filesToConfirm) {
        await this.ostUploadPersistenceService.confirmOstUploadFile(ownerId, ostNum, fileName);
      }
    } catch (error) {
      console.error("Failed to reconcile confirmed OST files:", error);
    }
  }

  private scheduleDeadline(files: AwaitingOstUploadEventFile[]): void {
    window.clearTimeout(this.expiryTimer);
    const nextDeadline = files.reduce<number | undefined>((nearest: number | undefined, file: AwaitingOstUploadEventFile): number | undefined => {
      if (file.deadlineAt === undefined) return nearest;
      return nearest === undefined || file.deadlineAt < nearest ? file.deadlineAt : nearest;
    }, undefined);
    if (nextDeadline === undefined) return;
    const ownerId = this.currentOwnerId;
    this.expiryTimer = window.setTimeout(
      () => {
        if (ownerId) void this.refreshOwnerState(ownerId);
      },
      Math.max(0, nextDeadline - Date.now())
    );
  }

  private scheduleDelivery(file: PendingOstUploadFile, minimumDelayMs = 0): void {
    if (!this.currentOwnerId || file.ownerId !== this.currentOwnerId || this.activeAttempts.has(this.ostKey(file)) || !navigator.onLine) return;
    window.clearTimeout(this.retryTimers.get(file.id));
    const delay = Math.max(minimumDelayMs, file.nextAttemptAt - Date.now(), 0);
    this.retryTimers.set(
      file.id,
      window.setTimeout(() => {
        this.retryTimers.delete(file.id);
        void this.deliverOst(file.ownerId, file.ostNum);
      }, delay)
    );
  }

  private ostKey(file: { ownerId: string; ostNum: string }): string {
    return JSON.stringify([file.ownerId, file.ostNum]);
  }

  /** Sends every due file of one OST in a single multipart request; parallel per-file uploads to the same OST were rejected with 400. */
  private async deliverOst(ownerId: string, ostNum: string): Promise<void> {
    const key = this.ostKey({ ownerId, ostNum });
    if (ownerId !== this.currentOwnerId || !navigator.onLine || this.activeAttempts.has(key)) return;
    this.activeAttempts.add(key);
    let lockUnavailable = false;
    let records: PendingOstUploadFile[] = [];
    try {
      const lockResult = await this.withSubmissionLock(key, ownerId, async () => {
        const snapshot = await this.ostUploadPersistenceService.getOstUploadSnapshot(ownerId);
        if (ownerId !== this.currentOwnerId) return;
        const now = Date.now();
        records = snapshot.delivery.filter((file: PendingOstUploadFile): boolean => file.ostNum === ostNum && file.nextAttemptAt <= now);
        if (records.length === 0) return;
        records.forEach((record: PendingOstUploadFile): void => {
          window.clearTimeout(this.retryTimers.get(record.id));
          this.retryTimers.delete(record.id);
        });
        await this.sendBatch(key, ostNum, records);
      });
      lockUnavailable = !lockResult.acquired;
    } catch (error) {
      console.error("Failed to submit persisted OST images:", error);
      await this.recordBatchFailure(records, error);
    } finally {
      this.activeAttempts.delete(key);
      if (ownerId === this.currentOwnerId) {
        await this.refreshOwnerState(ownerId);
        if (lockUnavailable) {
          const snapshot = await this.ostUploadPersistenceService.getOstUploadSnapshot(ownerId);
          snapshot.delivery.filter((record: PendingOstUploadFile): boolean => record.ostNum === ostNum).forEach((record): void => this.scheduleDelivery(record, 5_000));
        }
      }
    }
  }

  private async sendBatch(requestId: string, ostNum: string, records: PendingOstUploadFile[]): Promise<void> {
    const files = records.map((record: PendingOstUploadFile): File => new File([record.content], record.fileName, { type: record.type, lastModified: record.lastModified }));
    await new Promise<void>((resolve: () => void) => {
      let responseReceived = false;
      let finished = false;
      const finish = (): void => {
        if (finished) return;
        finished = true;
        this.activeRequests.delete(requestId);
        resolve();
      };
      const activeRequest: ActiveUploadRequest = { finish };
      this.activeRequests.set(requestId, activeRequest);

      const subscription = this.fileImageService
        .uploadImages(ostNum, files)
        .pipe(timeout({ first: UPLOAD_REQUEST_TIMEOUT_MS }))
        .subscribe({
          next: () => {
            responseReceived = true;
            void Promise.all(
              records.map((record: PendingOstUploadFile) =>
                this.ostUploadPersistenceService
                  .acceptOstUploadFile(record.ownerId, record.id)
                  .catch((error: unknown) => this.recordDeliveryFailure(record.ownerId, record.id, error))
              )
            ).finally(finish);
          },
          error: (error: unknown) => {
            void this.recordBatchFailure(records, error).finally(finish);
          },
          complete: () => {
            if (!responseReceived) void this.recordBatchFailure(records, new Error("Upload completed without a response.")).finally(finish);
          },
        });
      activeRequest.subscription = subscription;
    });
  }

  private async recordBatchFailure(records: PendingOstUploadFile[], error: unknown): Promise<void> {
    for (const record of records) await this.recordDeliveryFailure(record.ownerId, record.id, error);
  }

  private async recordDeliveryFailure(ownerId: string, id: string, error: unknown): Promise<void> {
    try {
      const snapshot = await this.ostUploadPersistenceService.getOstUploadSnapshot(ownerId);
      const record = snapshot.delivery.find((file: PendingOstUploadFile): boolean => file.id === id);
      if (!record) return;
      const attemptCount = record.attemptCount + 1;
      const delay = Math.min(RETRY_BASE_MS * 2 ** Math.min(attemptCount - 1, 20), RETRY_MAX_MS);
      const nextAttemptAt = Date.now() + delay;
      const message = error instanceof Error ? error.message : String(error ?? "Upload failed");
      const updated = await this.ostUploadPersistenceService.markOstUploadFailure(ownerId, id, message, nextAttemptAt);
      if (updated && ownerId === this.currentOwnerId) {
        this.scheduleDelivery({ ...record, attemptCount, nextAttemptAt, lastError: message });
      }
    } catch (persistenceError) {
      console.error("Could not persist OST upload retry state:", persistenceError);
      if (ownerId === this.currentOwnerId) {
        window.setTimeout(() => void this.refreshOwnerState(ownerId), 5_000);
      }
    }
  }

  private async withSubmissionLock<T>(id: string, ownerId: string, action: () => Promise<T>): Promise<{ acquired: boolean; value?: T }> {
    const lockManager = typeof navigator === "undefined" ? undefined : (navigator as Navigator & { locks?: BrowserLockManager }).locks;
    if (lockManager) {
      let acquired = false;
      const value = await lockManager.request(`ost-image-upload:${ownerId}:${id}`, { mode: "exclusive", ifAvailable: true }, async (lock: unknown | null) => {
        if (!lock) return undefined;
        acquired = true;
        return action();
      });
      return { acquired, value };
    }

    if (!(await this.ostUploadPersistenceService.acquireOstUploadLease(id, this.tabId, Date.now(), UPLOAD_LEASE_MS))) return { acquired: false };
    try {
      return { acquired: true, value: await action() };
    } finally {
      await this.ostUploadPersistenceService.releaseOstUploadLease(id, this.tabId);
    }
  }

  private async refreshDirtyUsage(ownerId: string, ostNum: string): Promise<void> {
    const dirtyId = JSON.stringify([ownerId, ostNum]);
    if (ownerId !== this.currentOwnerId || ostNum !== this.visibleOstNum || this.refreshingUsage.has(dirtyId)) return;
    this.refreshingUsage.add(dirtyId);
    try {
      await new Promise<void>((resolve: () => void, reject: (reason: unknown) => void) => {
        this.fileImageBucketStore.getBucketUsageByOstNum(ostNum).subscribe({
          next: () => resolve(),
          error: (error: unknown) => reject(error),
        });
      });
      await this.ostUploadPersistenceService.setOstUploadUsageClean(ownerId, ostNum);
    } catch {
      // Keep the durable dirty marker so the next visit can retry.
    } finally {
      this.refreshingUsage.delete(dirtyId);
    }
  }

  private newLocalId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
